import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useForm, type FieldPath } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CONTACT_FORM_TYPES,
  CONTACT_FORM_TYPE_LABELS,
  CONTACT_REFERRAL_SOURCES,
  CONTACT_REFERRAL_SOURCE_LABELS,
  MAX_EMAIL_LENGTH,
  MAX_NOTE_LENGTH,
  MAX_PERSON_NAME_LENGTH,
  MAX_PHONE_LENGTH,
  MAX_SHORT_TEXT_LENGTH,
  contactFormSchema,
  type ContactFormType,
  type ContactReferralSource,
} from '@trenigo/shared';
import { createSubmissionId, submitContactForm } from '../lib/contact-api.js';

// Sekce „Kontakt“ landing page (FR02 20261001_LandingPage/002_Marketing, FR-10, FR-11): formulář
// „Napište nám“ / „Požádat o demo“, který posílá zprávu jen provozovateli Trenigo přes POST
// /api/contact. Sekce je v předrenderovaném HTML; odeslání funguje po hydrataci.
// - submissionId vzniká až po hydrataci (useEffect), aby se předrenderované HTML a hydratace shodovaly
//   a každé zobrazení formuláře mělo vlastní identifikátor (opakované odeslání = jedna zpráva).
// - Honeypot `website` je mimo obrazovku, bez tabulátoru a skrytý čtečkám (FR-06).

export const CONTACT_SUCCESS_MESSAGE = 'Děkujeme, ozveme se vám co nejdříve.';
export const CONTACT_RATE_LIMITED_MESSAGE = 'Odesíláte příliš často. Zkuste to prosím později.';
export const CONTACT_ERROR_MESSAGE = 'Zprávu se nepodařilo odeslat. Zkuste to prosím později.';
export const CONTACT_PRIVACY_NOTE =
  'Údaje z formuláře použijeme jen k odpovědi na váš dotaz. Údaj o doporučení použijeme k případné odměně doporučitele.';

interface ContactFormValues {
  type: ContactFormType;
  name: string;
  email: string;
  phone: string;
  organization: string;
  // Zdroj poptávky a doporučitel (FR01 20261022_ContactReferral, FR-05): nepovinné, '' = nevyplněno.
  referralSource: ContactReferralSource | '';
  referredBy: string;
  message: string;
  website: string;
  submissionId: string;
}

const VISIBLE_FIELDS = ['type', 'name', 'email', 'phone', 'organization', 'referralSource', 'referredBy', 'message'] as const;
type VisibleField = (typeof VISIBLE_FIELDS)[number];

function isVisibleField(field: string): field is VisibleField {
  return (VISIBLE_FIELDS as readonly string[]).includes(field);
}

const DEFAULT_VALUES: ContactFormValues = {
  type: 'CONTACT',
  name: '',
  email: '',
  phone: '',
  organization: '',
  referralSource: '',
  referredBy: '',
  message: '',
  website: '',
  submissionId: '',
};

function RequiredMark() {
  return (
    <span className="contact-required" aria-hidden="true">
      *
    </span>
  );
}

function Field({
  id,
  label,
  required,
  error,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className={`contact-field${error ? ' has-error' : ''}`}>
      <label htmlFor={id}>
        {label}
        {required ? <RequiredMark /> : null}
      </label>
      {children}
      {error ? (
        <p className="contact-field-error" id={`${id}-error`}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

// `demoRequest` se zvýší při každém kliknutí na „Požádat o demo“ mimo formulář (CTA panel v
// LandingPage.tsx): odroluje na sekci, přepne formulář na typ DEMO a přesune kurzor do pole Jméno.
// Odkaz sám vede na #kontakt, takže bez JavaScriptu zůstane jen nativní odskok na sekci.
export function ContactSection({ demoRequest = 0 }: { demoRequest?: number } = {}) {
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const confirmationRef = useRef<HTMLDivElement>(null);
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    trigger,
    watch,
    formState: { errors, isSubmitting, isSubmitted, touchedFields },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: DEFAULT_VALUES,
    mode: 'onBlur',
  });

  useEffect(() => {
    setValue('submissionId', createSubmissionId());
  }, [setValue]);

  useEffect(() => {
    if (sent) confirmationRef.current?.focus();
  }, [sent]);

  useEffect(() => {
    if (demoRequest === 0) return;
    setValue('type', 'DEMO');
    const reducedMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.getElementById('kontakt')?.scrollIntoView?.({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    // Fokus nesmí odskok přerušit.
    document.getElementById('contact-name')?.focus({ preventScroll: true });
  }, [demoRequest, setValue]);

  const messageRequired = watch('type') === 'CONTACT';

  async function onSubmit(values: ContactFormValues) {
    setFormError(null);
    const result = await submitContactForm({ ...values });
    if (result.kind === 'ok') {
      setSent(true);
      return;
    }
    if (result.kind === 'rate_limited') {
      setFormError(CONTACT_RATE_LIMITED_MESSAGE);
      return;
    }
    if (result.kind === 'validation') {
      let shownOnField = false;
      for (const [field, messages] of Object.entries(result.fieldErrors)) {
        if (isVisibleField(field) && messages[0]) {
          setError(field as FieldPath<ContactFormValues>, { type: 'server', message: messages[0] });
          shownOnField = true;
        }
      }
      if (shownOnField) return;
    }
    setFormError(CONTACT_ERROR_MESSAGE);
  }

  function fieldProps(field: VisibleField) {
    const error = errors[field]?.message;
    return {
      id: `contact-${field}`,
      'aria-invalid': error ? true : undefined,
      'aria-describedby': error ? `contact-${field}-error` : undefined,
    };
  }

  return (
    <section className="section contact" id="kontakt" aria-labelledby="kontakt-nadpis">
      <div className="container contact-grid">
        <div className="contact-copy">
          <span className="eyebrow">
            <span className="eyebrow-dot" /> Kontakt
          </span>
          <h2 id="kontakt-nadpis">Napište nám, nebo si domluvte ukázku.</h2>
          <p>
            Máte dotaz k Trenigu, nebo chcete aplikaci vidět v provozu? Vyplňte formulář a ozveme se vám e-mailem.
          </p>
        </div>

        <div className="contact-card">
          {sent ? (
            <div className="contact-success" role="status" tabIndex={-1} ref={confirmationRef}>
              <span className="contact-success-icon" aria-hidden="true">
                ✓
              </span>
              <p>{CONTACT_SUCCESS_MESSAGE}</p>
            </div>
          ) : (
            <form className="contact-form" onSubmit={handleSubmit(onSubmit)} noValidate aria-label="Kontaktní formulář">
              <fieldset className="contact-type">
                <legend>Typ zprávy</legend>
                <div className="contact-type-options">
                  {CONTACT_FORM_TYPES.map((type) => (
                    <label className="contact-type-option" key={type}>
                      <input
                        type="radio"
                        value={type}
                        {...register('type', {
                          // Povinnost zprávy se mění s typem - existující chybu pole přepočítá hned.
                          onChange: () => {
                            if (isSubmitted || touchedFields.message) void trigger('message');
                          },
                        })}
                      />
                      <span>{CONTACT_FORM_TYPE_LABELS[type]}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="contact-row">
                <Field id="contact-name" label="Jméno" required error={errors.name?.message}>
                  <input
                    type="text"
                    autoComplete="name"
                    maxLength={MAX_PERSON_NAME_LENGTH}
                    aria-required="true"
                    {...fieldProps('name')}
                    {...register('name')}
                  />
                </Field>
                <Field id="contact-email" label="E-mail" required error={errors.email?.message}>
                  <input
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    maxLength={MAX_EMAIL_LENGTH}
                    aria-required="true"
                    {...fieldProps('email')}
                    {...register('email')}
                  />
                </Field>
              </div>

              <div className="contact-row">
                <Field id="contact-phone" label="Telefon" error={errors.phone?.message}>
                  <input type="tel" autoComplete="tel" maxLength={MAX_PHONE_LENGTH} {...fieldProps('phone')} {...register('phone')} />
                </Field>
                <Field id="contact-organization" label="Akademie / oddíl" error={errors.organization?.message}>
                  <input
                    type="text"
                    autoComplete="organization"
                    maxLength={MAX_SHORT_TEXT_LENGTH}
                    {...fieldProps('organization')}
                    {...register('organization')}
                  />
                </Field>
              </div>

              {/* Zdroj poptávky a doporučitel (FR01 20261022_ContactReferral, FR-05): nepovinné a na
                  sobě nezávislé, pro oba typy zprávy. */}
              <div className="contact-row">
                <Field id="contact-referralSource" label="Jak jste se o nás dozvěděli?" error={errors.referralSource?.message}>
                  <select {...fieldProps('referralSource')} {...register('referralSource')}>
                    <option value="">Vyberte…</option>
                    {CONTACT_REFERRAL_SOURCES.map((source) => (
                      <option key={source} value={source}>
                        {CONTACT_REFERRAL_SOURCE_LABELS[source]}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field id="contact-referredBy" label="Kdo nás doporučil?" error={errors.referredBy?.message}>
                  <input
                    type="text"
                    autoComplete="off"
                    maxLength={MAX_SHORT_TEXT_LENGTH}
                    {...fieldProps('referredBy')}
                    {...register('referredBy')}
                  />
                </Field>
              </div>

              <Field id="contact-message" label="Zpráva" required={messageRequired} error={errors.message?.message}>
                <textarea
                  rows={5}
                  maxLength={MAX_NOTE_LENGTH}
                  aria-required={messageRequired ? 'true' : undefined}
                  {...fieldProps('message')}
                  {...register('message')}
                />
              </Field>

              {/* Honeypot (FR-06): člověk pole nevidí ani na něj neodtabuje. */}
              <div className="contact-hp" aria-hidden="true">
                <label htmlFor="contact-website">Web</label>
                <input id="contact-website" type="text" tabIndex={-1} autoComplete="off" {...register('website')} />
              </div>

              {formError ? (
                <p className="contact-form-error" role="alert">
                  {formError}
                </p>
              ) : null}

              <div className="contact-actions">
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Odesílám…' : 'Odeslat'}
                  {isSubmitting ? null : <span className="arrow">→</span>}
                </button>
                <p className="contact-privacy">{CONTACT_PRIVACY_NOTE}</p>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
